import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import Checkbox from 'monday-ui-react-core/dist/Checkbox';
import TextField from 'monday-ui-react-core/dist/TextField';
import Dropdown from 'monday-ui-react-core/dist/Dropdown';
import Button from 'monday-ui-react-core/dist/Button';
import Exclamation from '../../assets/icons/exclamation.svg';
import styled from 'styled-components';

import useLineItemSettings from '../LineItems/Hooks/useLineItemSettings';
import { getSubItems } from '../../services/boards';
import { API_ROUTES } from '@/constants/api-routes';
import { API_URL_V3 } from '@/constants/constants';
import { generateAuthHeaders } from '@/utils/generate-auth-headers';
import { subitemsHaveColumnValues } from '@/utils/resolve-subitem-sample';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import CustomColorPicker from '../CustomColorPicker/CustomColorPicker';
import { useTranslation } from 'react-i18next';
import useCkSubitems from './useCkSubitems';
import { CK_SUBITEM_EVENTS } from '../../config/common';
import { FileContext } from '../../context/FileContext';
import APP_CONFIG from '../../config';
import { Controller, FormProvider } from 'react-hook-form';
import SubItemFilter from '../SubItemFilters';
import Loader from 'monday-ui-react-core/dist/Loader';
import { isFeatureAvailable, FEATURES } from '../../constants/featureBasedPricing';

const Header = styled.h2`
  color: ${({ color }) => color};
`;

const Labels = styled.p`
  padding: var(--spacing-xs) 0;
  font-size: 14px;
  color: #323338;
`;

const ErrorMessage = styled.p`
  font-size: 12px;
  color: red;
  padding: 0;
  margin: 0;
`;

const UpgradeButton = styled(Button)`
  // background: #ffd383 !important;
  // color: #000;

  margin-top: 40px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
`;
const ControllerSettingsContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  background: var(--light-primary-background-color, #fff);
  padding: 18px 14px 18px 14px;

  .error {
    border: 1px solid red !important;
  }

  .hl {
    height: 1px;
    width: 100%;
    background-color: var(--ui-border-color);
  }

  .text {
    color: var(--light-primary-text-color, #323338);
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon {
      cursor: pointer;
    }
  }

  .info-icon {
    width: fit-content;
  }

  .content {
    margin: 20px 0 20px 0;
    display: flex;
    gap: 30px;

    @media only screen and (max-width: 768px) {
      flex-direction: column;
    }

    .col {
      width: 25%;
      display: flex;
      flex-direction: column;
      gap: 30px;
      @media only screen and (max-width: 768px) {
        width: 100%;
      }
      .item {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }
    }
  }

  .content.alert {
    gap: 10px;
  }

  .content img {
    margin-bottom: 15px;
  }

  .flex-col {
    display: flex;
    flex-direction: column;
  }

  .flex-col.items-center {
    align-items: center;
  }

  .negative-text {
    color: var(--negative-color);
  }

  .alert-header {
    font-size: 18px;
    font-weight: 500;
    line-height: 20px;
  }

  .alert-message {
    text-align: center;
  }

  .subitem-filter-container {
    padding: var(--spacing-medium) 0;
    position: relative;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #676879;
      border-radius: 1px;
      height: 25px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgb(148, 149, 159);
    }

    &::-webkit-scrollbar-track {
      background: #f0f0f0;
    }
  }
  .actions {
    text-align: right;
    .button {
      width: 48px;
    }
  }
`;

const SelectColumnsContainer = styled.div`
  height: max-height;
  overflow-y: scroll;
  /* Ensure scrollbar is visible and styled */
  &::-webkit-scrollbar {
    width: 4px; /* Adjust width */
  }

  &::-webkit-scrollbar-thumb {
    background: #676879; /* Scrollbar color */
    border-radius: 1px;
    height: 25px; /* Adjust thumb height */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgb(148, 149, 159); /* Slightly darker on hover */
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0; /* Track color to make it stand out */
  }
`;

const fetchSubitemBoardParentId = async (subitemBoardId) => {
  const url = API_ROUTES.MONDAY_GET_SUBITEM_BOARD_PARENT.replace(
    ':subitemBoardId',
    String(subitemBoardId),
  );
  const { data } = await axios({
    baseURL: API_URL_V3,
    url,
    method: 'GET',
    headers: generateAuthHeaders(),
  });
  return data?.data?.parentItemId ?? null;
};

const CK_SUBITEM = ({
  editorRef,
  mode = CK_SUBITEM_EVENTS.create,
  subItemConfigurations,
  fileId,
  closeModal,
  onSaveCallback,
  defaultValue = null,
  fallbackSubitemBoardId = null,
  itemId: itemIdProp,
}) => {
  const params = useParams();
  const resolvedItemId = itemIdProp ?? params?.itemId;
  const { t } = useTranslation();

  const { handleSaveConfig, filterFormMethods, updateFilters } = useCkSubitems({
    editorRef,
    mode,
    subItemConfigurations,
    fileId,
    defaultValue,
    onSaveCallback,
  });

  const { watch, setValue, trigger } = filterFormMethods;

  const isSumEnabled = watch('sum.is_shown');
  const isTaxEnabled = watch('tax.is_shown');
  const isCustomizedUnitEnabled = watch('customizedUnit.is_shown');
  const isHeaderEnabled = watch('header.is_shown');

  const { pricingPlan, slug } = useContext(FileContext);

  const isDisabled = !isFeatureAvailable(pricingPlan, FEATURES.LINE_ITEMS);

  const taxTypeOptions = useMemo(
    () => [
      { value: 'percentage', label: `${t('Percentage')} (%)` },
      { value: 'fixed', label: t('Fixed') },
    ],
    [t],
  );

  const currencyPositionOptions = useMemo(
    () => [
      { value: 'before', label: t('Before the value') },
      { value: 'after', label: t('After the value') },
    ],
    [t],
  );

  const currencyTypesOptions = useMemo(
    () => [
      { value: 'usd', label: 'USD', symbol: '$' },
      { value: 'gbp', label: 'GBP', symbol: '£' },
      { value: 'eur', label: 'EUR', symbol: '€' },
      { value: 'aud', label: 'AUD', symbol: 'AUD' },
      { value: 'cad', label: 'CAD', symbol: 'CAD' },
      { value: 'zar', label: 'ZAR', symbol: 'R' },
      { value: 'ils', label: 'ILS', symbol: '₪' },
    ],
    [],
  );

  const rgbMapper = {
    '#040930': { r: 4, g: 9, b: 48 },
    '#396AFF': { r: 57, g: 106, b: 255 },
    '#0DB691': { r: 13, g: 182, b: 145 },
    '#FF8282': { r: 255, g: 130, b: 130 },
    '#647E9E': { r: 100, g: 126, b: 158 },
    '#FF2929': { r: 255, g: 41, b: 41 },
    '#A09C9C': { r: 160, g: 156, b: 156 },
    '#FFC107': { r: 255, g: 193, b: 7 },
    '#93A689': { r: 147, g: 166, b: 137 },
    '#F5F6F8': { r: 245, g: 246, b: 248 },
  };

  const hasRealResolvedItemId =
    !!resolvedItemId &&
    resolvedItemId !== 0 &&
    resolvedItemId !== '0';

  const { isLoading, data: subItemsResponse } = useQuery(
    ['sub-items', resolvedItemId, fallbackSubitemBoardId],
    async () => {
      if (hasRealResolvedItemId) {
        const first = await getSubItems(resolvedItemId);
        const subitems = first?.data?.items?.[0]?.subitems;
        if (
          subitemsHaveColumnValues(subitems) ||
          !fallbackSubitemBoardId
        ) {
          return first;
        }
      } else if (!fallbackSubitemBoardId) {
        return { data: { items: [{ subitems: [] }] } };
      }
      const parentItemId = await fetchSubitemBoardParentId(
        fallbackSubitemBoardId,
      );
      if (!parentItemId) {
        return hasRealResolvedItemId
          ? getSubItems(resolvedItemId)
          : { data: { items: [{ subitems: [] }] } };
      }
      return getSubItems(parentItemId);
    },
    {
      enabled: hasRealResolvedItemId || !!fallbackSubitemBoardId,
      staleTime: 1000,
    },
  );

  const { columnOptions, setDefaultSubItems } = useLineItemSettings();

  useEffect(() => {
    if (isLoading) return;
    if (subItemsResponse?.data?.items?.[0]?.subitems?.length) {
      setDefaultSubItems(subItemsResponse?.data?.items?.[0]?.subitems);
    }
  }, [subItemsResponse, setDefaultSubItems, isLoading]);

  const customizedColumnsOptions = useMemo(() => {
    return columnOptions
      ?.filter((x) => ['formula', 'numeric', 'numbers'].includes(x.type))
      ?.map((x) => ({
        value: x.id,
        label: x.title || x.label,
      }));
  }, [columnOptions]);

  if (isDisabled) {
    return (
      <ControllerSettingsContainer>
        <div className="content flex-col items-center alert">
          <img
            alt="alert"
            src={Exclamation}
            height={48}
            className="alert-img"
          />

          <p className="alert-header">
            {t('This feature is not part of your plan')}
          </p>

          <p className="alert-message">
            {t(
              'Click below to learn about Getsign plans and consider upgrading',
            )}
          </p>
          <UpgradeButton
            size={Button.sizes.SMALL}
            kind={Button.kinds.PRIMARY}
            // submitButtonColor="#FFD383"
            // submitButtonTextColor="#000"
            className="button"
            onClick={() => {
              window.open(
                `https://${slug}.monday.com/apps/installed_apps/10050849`,
              );
            }}
          >
            {t('Upgrade')}
          </UpgradeButton>
        </div>
      </ControllerSettingsContainer>
    );
  }

  const selectedColumns = watch('selectedColumns');

  return (
    <FormProvider {...filterFormMethods}>
      <ControllerSettingsContainer>
        <Header>{t('Configure sub items')}</Header>

        <div className="content">
          <div className="col">
            <div className="item">
              <Controller
                name="sum.is_shown"
                render={({ field }) => (
                  <Checkbox
                    disabled={isDisabled}
                    label={t('Show Sum at the bottom')}
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);

                      !e.target.checked && trigger('sum');
                    }}
                  />
                )}
              />

              <Controller
                name="sum.label"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <TextField
                      className={error?.message ? 'error' : ''}
                      placeholder="Sum"
                      disabled={!isSumEnabled}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e)}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />

              <Controller
                name="sum.column"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <Dropdown
                      className={error?.message ? 'error' : ''}
                      placeholder={t('Set Column to Sum')}
                      disabled={!isSumEnabled}
                      options={customizedColumnsOptions}
                      value={field.value}
                      size={Dropdown.size.SMALL}
                      onChange={(value) => field.onChange(value)}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
            </div>
            <div className="item">
              <Controller
                name="header.is_shown"
                render={({ field }) => (
                  <Checkbox
                    checked={!!field.value}
                    label={t('Change header background')}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      !e.target.checked && trigger('header');
                    }}
                  />
                )}
              />
              <Controller
                name="header.color"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <CustomColorPicker
                      error={error?.message || ''}
                      value={field.value}
                      disabled={!isHeaderEnabled}
                      handleInput={(val) => {
                        field.onChange(val.toString());
                        setValue('header.rgb', rgbMapper[val.toString()]);
                      }}
                    />

                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
            </div>
          </div>

          <div className="col">
            <div className="item">
              <Controller
                name="tax.is_shown"
                render={({ field }) => (
                  <Checkbox
                    label={t('Show Tax at the bottom')}
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      !e.target.checked && trigger('tax');
                    }}
                  />
                )}
              />
              <Controller
                name="tax.label"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <TextField
                      className={error?.message ? 'error' : ''}
                      placeholder="Tax"
                      onChange={(e) => field.onChange(e)}
                      value={field.value ?? ''}
                      disabled={!isTaxEnabled}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />

              <Controller
                name="tax.type"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <Dropdown
                      className={error?.message ? 'error' : ''}
                      placeholder={'Type'}
                      options={taxTypeOptions}
                      size={Dropdown.size.SMALL}
                      value={field.value}
                      disabled={!isTaxEnabled}
                      onChange={(e) => field.onChange(e)}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
              <Controller
                name="tax.value"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <TextField
                      placeholder={t('Value')}
                      disabled={!isTaxEnabled}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e)}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
            </div>
            <div className="item">
              <Controller
                name="customizedUnit.is_shown"
                render={({ field }) => (
                  <Checkbox
                    label={t('Customize Unit')}
                    checked={!!field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      !e.target.checked && trigger('customizedUnit');
                    }}
                  />
                )}
              />

              <Controller
                name="customizedUnit.value"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <Dropdown
                      placeholder={t('Select Currency')}
                      options={currencyTypesOptions}
                      size={Dropdown.size.SMALL}
                      disabled={!isCustomizedUnitEnabled}
                      onChange={(e) => field.onChange(e)}
                      value={field.value}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
              <Controller
                name="customizedUnit.position"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <Dropdown
                      className={error?.message ? 'error' : ''}
                      placeholder={t('Position')}
                      options={currencyPositionOptions}
                      size={Dropdown.size.SMALL}
                      value={field.value}
                      disabled={!isCustomizedUnitEnabled}
                      onChange={(e) => field.onChange(e)}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />

              <Controller
                name="customizedUnit.columns"
                render={({ field, fieldState: { error } }) => (
                  <FieldWrapper>
                    <Dropdown
                      className={error?.message ? 'error' : ''}
                      value={field.value || []}
                      placeholder={t('Show in Column')}
                      options={customizedColumnsOptions}
                      size={Dropdown.size.SMALL}
                      multi
                      multiline={true}
                      disabled={!isCustomizedUnitEnabled}
                      onOptionSelect={(e) => {
                        const fieldValue = [...field.value, e];
                        field.onChange(fieldValue);
                      }}
                      onClear={() => {
                        field.onChange([]);
                      }}
                      onOptionRemove={(value) => {
                        const fieldValue = field.value.filter(
                          (x) => x.value !== value.value,
                        );
                        field.onChange(fieldValue);
                      }}
                    />
                    <ErrorMessage>{error?.message || ''}</ErrorMessage>
                  </FieldWrapper>
                )}
              />
            </div>
          </div>

          <div className="col">
            <div className="item">
              <SelectColumnsContainer>
                <div className="text">{t('Select Columns')}</div>
                <Checkbox label={t('Item')} checked disabled />

                {isLoading ? (
                  <Loader size={Loader.sizes.SMALL} />
                ) : (
                  columnOptions?.map((opt) => (
                    <Checkbox
                      label={opt.label || ''}
                      key={opt.id}
                      checked={!!selectedColumns?.find((x) => x.id === opt.id)}
                      disabled={opt.type === 'file'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue('selectedColumns', [
                            ...(selectedColumns || []),
                            { id: opt.id, label: opt.label },
                          ]);
                        } else {
                          setValue(
                            'selectedColumns',
                            selectedColumns?.filter((x) => x.id !== opt.id),
                          );
                        }
                      }}
                    />
                  ))
                )}
              </SelectColumnsContainer>
            </div>
          </div>
        </div>

        <div className="hl"></div>
        <div className="subitem-filter-container">
          <Controller
            control={filterFormMethods.control}
            name="isFilterEnabled"
            render={({ field }) => (
              <Checkbox
                label={t('Filter subitems')}
                checked={!!field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                }}
              />
            )}
          />

          <Labels>
            Filter subitems appearing in your document based on the following
            conditions:
          </Labels>

          <SubItemFilter
            updateFilters={updateFilters}
            itemId={
              subItemsResponse?.data?.items?.[0]?.id ?? resolvedItemId
            }
          />
        </div>

        <div className="actions">
          <Button
            disabled={isDisabled}
            size={Button.sizes.SMALL}
            kind={Button.kinds.PRIMARY}
            className="button"
            onClick={() => {
              filterFormMethods.handleSubmit(
                (data) => {
                  handleSaveConfig(data);
                  closeModal();
                },
                (errors) => console.log({ errors }),
              )();
            }}
          >
            {t('Save')}
          </Button>
        </div>
      </ControllerSettingsContainer>
    </FormProvider>
  );
};

export default CK_SUBITEM;
